import React, { ReactNode, forwardRef } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  label?: string;
  variant?: "primary" | "secondary" | "tertiary";
  size?: "xs" | "sm" | "md" | "lg";
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    label,
    variant = "primary",
    size = "md",
    disabled = false,
    className = "",
    leadingIcon,
    trailingIcon,
    ...rest
  },
  ref,
) {
  const getVariantClasses = () => {
    switch (variant) {
      case "primary":
        return "bg-prime text-white hover:bg-[color-mix(in_srgb,var(--prime)_90%,black)] active:bg-[color-mix(in_srgb,var(--prime)_80%,black)]";
      case "secondary":
        return "bg-bg-elevated text-fg font-normal border border-bg2 hover:bg-bg2 hover:border-bg2 active:bg-bg3";
      case "tertiary":
        return "bg-transparent text-fg2 hover:bg-bg2 active:bg-bg3";
      default:
        return "bg-prime text-white hover:bg-[color-mix(in_srgb,var(--prime)_90%,black)] active:bg-[color-mix(in_srgb,var(--prime)_80%,black)]";
    }
  };

  const getSizeClasses = () => {
    const isIconButton = !label && leadingIcon && !trailingIcon && !children;
    switch (size) {
      case "xs":
        return isIconButton ? "h-7 w-7 p-1" : "h-7 px-2 py-1 text-xs";
      case "sm":
        return isIconButton ? "h-8 w-8 p-1.5" : "h-8 px-4 py-1.5 text-[13px]";
      case "md":
        return isIconButton ? "h-10 w-10 p-2" : "h-10 px-4 py-2 text-sm";
      case "lg":
        return isIconButton ? "h-11 w-11 p-2.5" : "h-11 px-6 py-3 text-base";
      default:
        return isIconButton ? "h-10 w-10 p-2" : "h-10 px-4 py-2 text-sm";
    }
  };

  const getDisabledClasses = () => {
    if (disabled) {
      return "opacity-50 cursor-not-allowed text-fg3";
    }
    return "cursor-pointer";
  };

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={`rounded-md inline-flex justify-center items-center gap-2 font-medium leading-snug transition-colors ${getVariantClasses()} ${getSizeClasses()} ${getDisabledClasses()} ${className}`}
      {...rest}
    >
      {leadingIcon && <div className="flex-shrink-0">{leadingIcon}</div>}
      {children}
      {label && <span>{label}</span>}
      {trailingIcon && <div className="flex-shrink-0">{trailingIcon}</div>}
    </button>
  );
});

export default Button;
